import { useReducer, useEffect } from 'react'
import api, { Role, IEmployeeDetails } from '@api'
import { Toaster } from '@ui'
import { ApolloError } from 'apollo-boost'

enum Actions {
  START_LOAD,
  LOAD,
  UPDATE,
  ROLE_CHANGE,
  NOTE_CHANGE,
  REMOTE_TOGGLE,
  NO_DESK_NEEDED_TOGGLE,
  MANAGE_ALL,
}

interface StartLoadAction {
  type: Actions.START_LOAD
}
interface LoadAction {
  type: Actions.LOAD
  employee: IEmployeeDetails
}

interface RoleChangeAction {
  type: Actions.ROLE_CHANGE
  role: Role
}

interface ManageAllAction {
  type: Actions.MANAGE_ALL
  isManageAll: boolean
}

interface NoteChangeAction {
  type: Actions.NOTE_CHANGE
  note: string
}

interface RemoteToggleAction {
  type: Actions.REMOTE_TOGGLE
  isRemote: boolean
}

interface NoDeskNeededToggleAction {
  type: Actions.NO_DESK_NEEDED_TOGGLE
  noDeskNeeded: boolean
}

type Action =
  | StartLoadAction
  | LoadAction
  | RoleChangeAction
  | ManageAllAction
  | RemoteToggleAction
  | NoteChangeAction
  | NoDeskNeededToggleAction

interface IState {
  isLoading: boolean
  employee: IEmployeeDetails | null
}

interface UseEmployeeDetailsHook extends IState {
  update: (employee: IEmployeeDetails) => void
  updateRole: (role: Role) => void
  updateBookingManagerManageAll: (isManageAll: boolean) => void
  updateNote: (note: string) => void
  toggleRemote: (isRemote: boolean) => void
  toggleNoDeskNeeded: (noDeskNeeded: boolean) => void
}

let _details: IEmployeeDetails | null = null

export default function useEmployeeDetails(id?: string): UseEmployeeDetailsHook {
  const [state, dispatch] = useReducer(reducer, {
    isLoading: !_details || _details.id !== id,
    employee: _details && _details.id === id ? _details : null,
  })

  useEffect(() => {
    if (id) {
      dispatch({ type: Actions.START_LOAD })
      api.employee
        .details(id)
        .then((employee: IEmployeeDetails) => {
          dispatch({ type: Actions.LOAD, employee })
        })
        .catch((error: ApolloError) => Toaster.fail({ error }))
    }
  }, [id])

  useEffect(() => {
    _details = state.employee
  })

  return {
    ...state,
    update: (employee: IEmployeeDetails) => {
      if (state.employee) {
        dispatch({
          type: Actions.LOAD,
          employee,
        })
      }
    },
    updateRole: (role: Role) => {
      dispatch({ type: Actions.ROLE_CHANGE, role })
    },
    updateNote: (note: string) => {
      dispatch({ type: Actions.NOTE_CHANGE, note })
    },
    toggleRemote: (isRemote: boolean) => {
      dispatch({ type: Actions.REMOTE_TOGGLE, isRemote })
    },
    toggleNoDeskNeeded: (noDeskNeeded: boolean) => {
      dispatch({ type: Actions.NO_DESK_NEEDED_TOGGLE, noDeskNeeded })
    },
    updateBookingManagerManageAll: (isManageAll: boolean) => {
      dispatch({ type: Actions.MANAGE_ALL, isManageAll })
    },
  }
}

function reducer(state: IState, action: Action): IState {
  switch (action.type) {
    case Actions.START_LOAD:
      return {
        isLoading: true,
        employee: null,
      }

    case Actions.LOAD:
      return {
        isLoading: false,
        employee: action.employee,
      }

    case Actions.ROLE_CHANGE:
      return state.employee
        ? {
            ...state,
            employee: {
              ...state.employee,
              role: action.role,
            },
          }
        : state
    case Actions.MANAGE_ALL:
      return state.employee
        ? {
            ...state,
            employee: {
              ...state.employee,
              user: {
                bookingManagerSetting: {
                  manage_all: action.isManageAll,
                },
              },
            },
          }
        : state

    case Actions.NOTE_CHANGE:
      return state.employee
        ? {
            ...state,
            employee: {
              ...state.employee,
              note: action.note,
            },
          }
        : state

    case Actions.REMOTE_TOGGLE:
      return state.employee
        ? {
            ...state,
            employee: {
              ...state.employee,
              isRemote: action.isRemote,
            },
          }
        : state
    case Actions.NO_DESK_NEEDED_TOGGLE:
      return state.employee
        ? {
            ...state,
            employee: {
              ...state.employee,
              noDeskNeeded: action.noDeskNeeded,
            },
          }
        : state

    default:
      return state
  }
}
